import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Stack,
    IconButton,
    Modal,
    Backdrop,
    Fade,
    Button,
    TextField,
    Menu,
    MenuItem
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ImageUpload from '../../components/UserAuthentication/ImageUpload';
import { addAd, getAd, deleteAd, checkAdminApi, checkTrainerApi, getUserById } from '../../utils/api';
import bitImg from '../../assets/icons/bit.png';

const Ad = ({ userId }) => {
    const [ads, setAds] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isTrainer, setIsTrainer] = useState(false);

    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [googleFormUrl, setGoogleFormUrl] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);  // For URL input form

    // States for context menu (triggered by right click / long press)
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedAdForMenu, setSelectedAdForMenu] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');

    const handlePaymentRedirect = () => {
        setSelectedAd(null);
        const bitPayUrlApp = `bitpay://pay?phone=${phoneNumber}`;
        const bitPayUrlWeb = `https://www.bitpay.co.il/pay?phone=${phoneNumber}`;

        window.location.href = bitPayUrlApp;
        setTimeout(() => {
            window.location.href = bitPayUrlWeb;
        }, 2000);
    };

    const fetchAds = async () => {
        try {
            const adminStatus = await checkAdminApi(userId);
            setIsAdmin(adminStatus);

            const trainerStatus = await checkTrainerApi(userId);
            setIsTrainer(trainerStatus);

            const user = await getUserById(userId);

            if (!user) {
                console.error('User not found');
                return;
            }

            const fetchId = user.role === "trainee" ? user.trainer_id : userId;
            if (user.role !== "trainer") {
                const trainerId = user.trainer_id;
                const trainer = await getUserById(trainerId);
                setPhoneNumber(trainer.phone);
            }
            else {
                setPhoneNumber(user.phone);
            }

            const response = await getAd(fetchId);
            const { ads } = response;
            const parsedAds = ads.map((ad) => {
                if (!ad.fileBlob) return { url: "null", googleFormUrl: "" };
                return {
                    ...ad,
                    url: `data:${ad.fileType};base64,${ad.fileBlob}`,
                };
            });
            setAds(parsedAds);
        } catch (error) {
            console.error("Error fetching ads:", error);
        }
    };

    useEffect(() => {
        fetchAds();
    }, []);

    const handleUpload = async (file) => {
        if (!googleFormUrl) {
            alert("Please enter a valid Google Form URL.");
            return;
        }
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('image', file);
        formData.append('googleFormUrl', googleFormUrl);
        try {
            await addAd(formData);
            fetchAds();
            setGoogleFormUrl('');
        } catch (error) {
            console.error('Error uploading ad:', error);
        }
    };

    const handleAdClick = (ad) => {
        setSelectedAd(ad);
    };

    const closeAdModal = () => {
        setSelectedAd(null);
    };

    // Handle right-click or long press to open the delete menu (for admins)
    const handleContextMenu = (event, ad) => {
        event.preventDefault();
        if (isAdmin) {
            setMenuAnchorEl(event.currentTarget);
            setSelectedAdForMenu(ad);
        }
    };

    const handleDeleteAd = async () => {
        if (!selectedAdForMenu) return;
        try {
            // Use the ad's id for deletion
            await deleteAd(selectedAdForMenu._id);
            fetchAds();
        } catch (error) {
            console.error('Error deleting ad:', error);
        } finally {
            setMenuAnchorEl(null);
            setSelectedAdForMenu(null);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                backgroundColor: '#FFFAFB',
                padding: '20px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    marginBottom: '20px',
                    gap: '10px',
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                }}
            >
                {(isAdmin || isTrainer) && (
                    <IconButton
                        onClick={() => setIsFormOpen(true)}
                        sx={{
                            color: '#B5A78A',
                            fontSize: '32px',
                        }}
                    >
                        <AddCircleIcon fontSize="large" />
                    </IconButton>
                )}

                <Typography
                    sx={{
                        fontSize: { lg: '32px', xs: '20px' },
                        fontWeight: 600,
                        color: '#B5A78A',
                        fontFamily: 'GveretLevin'
                    }}
                >
                    אירועים קבוצתיים
                </Typography>
            </Box>

            {/* Wrap your ads in a flex container for a gallery-like layout */}
            <Stack
                direction="row"
                flexWrap="wrap"
                gap="20px"
                justifyContent="center"
                alignItems="center"
                sx={{ marginBottom: '40px', width: '100%' }}
            >
                {ads.map((ad, index) => (
                    <Box
                        key={index}
                        sx={{ maxWidth: { lg: '320px', xs: '100%' }, cursor: 'pointer' }}
                        onClick={() => handleAdClick(ad)}
                        onContextMenu={(e) => handleContextMenu(e, ad)}
                    >
                        <img
                            src={ad.url}
                            alt="Ad"
                            style={{ width: '100%', borderRadius: '20px' }}
                        />
                    </Box>
                ))}
            </Stack>

            {/* Context Menu for deletion */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => {
                    setMenuAnchorEl(null);
                    setSelectedAdForMenu(null);
                }}
            >
                <MenuItem onClick={handleDeleteAd}>Delete Ad</MenuItem>
            </Menu>

            {/* Modal for adding a new ad */}
            <Modal
                open={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={isFormOpen}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            maxWidth: '600px',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            alignItems: "right",
                            direction: "rtl"
                        }}
                    >
                        <Typography variant="h6" mb={2}>
                            הוספת אירוע חדש
                        </Typography>
                        <TextField
                            value={googleFormUrl}
                            onChange={(e) => setGoogleFormUrl(e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{ mb: 2 }}
                            placeholder=" הדבק כאן את הקישור לפורם"
                            textAlign="right"
                            direction="rtl"
                            justifyContent="right"
                        />
                        <Typography variant="h7" alignItems="right" direction="rtl" mb={2}>
                            תמונת האירוע:
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setIsUploadOpen(true)}
                            sx={{ mb: 2, mr: "10px" }}
                        >
                            העלה כאן
                        </Button>
                        <ImageUpload
                            open={isUploadOpen}
                            onClose={() => setIsUploadOpen(false)}
                            onUpload={handleUpload}
                        />
                    </Box>
                </Fade>
            </Modal>

            {/* Modal to display selected ad and Google Form */}
            <Modal
                open={!!selectedAd}
                onClose={closeAdModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={!!selectedAd}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            maxWidth: '600px',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                        }}
                    >
                        {selectedAd && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 3,
                                        width: '100%',
                                    }}
                                >
                                    <img
                                        src={selectedAd.url}
                                        alt="Selected Ad"
                                        style={{
                                            width: '50%',
                                            marginBottom: '20px',
                                            borderRadius: '10px',
                                        }}
                                    />
                                    <Typography
                                        variant="h7"
                                        direction="rtl"
                                        textAlign="right"
                                        gutterBottom
                                        mb={"20px"}
                                    >
                                        (: מלאו את הפרטים שלכם ונחזור  אליכם.
                                    </Typography>
                                </Box>

                                <iframe
                                    src={selectedAd.googleFormUrl}
                                    width="100%"
                                    height="400"
                                    frameBorder="0"
                                    style={{ border: 'none' }}
                                    title="Google Form"
                                />

                                <Box mt={2} textAlign="center">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handlePaymentRedirect}
                                        startIcon={<img src={bitImg} alt="BitPay Icon" style={{ width: 24, height: 24 }} />}
                                    >
                                        לתשלום בביט
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default Ad;
